import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * Light reveal-on-scroll for elements marked with `data-lux-reveal`.
 *
 * - Observes Horizon's real scroll container through `getIntersectionRoot()`:
 *   `.page-wrapper` from 990px, the viewport below.
 * - Does nothing under `prefers-reduced-motion: reduce`, in the theme editor, or without
 *   IntersectionObserver, so content is never hidden in those cases.
 * - Elements already in view at load are revealed before anything is hidden (no flash).
 */

const SELECTOR = '[data-lux-reveal]';
const READY_CLASS = 'lux-reveal-ready';
const REVEALED_CLASS = 'lux-is-revealed';

/** @type {IntersectionObserver | undefined} */
let observer;

/**
 * @param {IntersectionObserverEntry[]} entries
 * @param {IntersectionObserver} io
 */
function onIntersect(entries, io) {
  for (const entry of entries) {
    if (!entry.isIntersecting) continue;
    entry.target.classList.add(REVEALED_CLASS);
    io.unobserve(entry.target);
  }
}

/** @returns {{ top: number, bottom: number }} Visible vertical range of the scroll container. */
function getVisibleRange() {
  const root = getIntersectionRoot();
  if (root) {
    const { top, bottom } = root.getBoundingClientRect();
    return { top, bottom };
  }
  return { top: 0, bottom: window.innerHeight };
}

function observeAll() {
  observer?.disconnect();
  observer = new IntersectionObserver(onIntersect, {
    root: getIntersectionRoot(),
    rootMargin: '0px 0px -8% 0px',
  });

  for (const element of document.querySelectorAll(`${SELECTOR}:not(.${REVEALED_CLASS})`)) {
    observer.observe(element);
  }
}

function init() {
  const html = document.documentElement;
  const skip =
    html.classList.contains('shopify-design-mode') ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    !('IntersectionObserver' in window);

  if (skip) return;

  const { top, bottom } = getVisibleRange();
  for (const element of document.querySelectorAll(SELECTOR)) {
    const rect = element.getBoundingClientRect();
    if (rect.top < bottom && rect.bottom > top) element.classList.add(REVEALED_CLASS);
  }

  html.classList.add(READY_CLASS);
  observeAll();

  // The scroll container changes at 990px (.page-wrapper vs viewport): rebuild the observer.
  scrollContainerMediaQuery.addEventListener('change', observeAll);
}

init();
